<template>
  <div>
    <div class="navless-layout-header">
      <slot name='navbar'>
        <ed-button :to='$routerHistory.previous().path'>
          <i class="fal fa-chevron-left"></i>
          &nbsp;
          Back
        </ed-button>
        <span v-if="cardRejected" class="center-span">{{ cards.createdDate ? 'EvedPay... ' + moment(cards.createdDate).format(dateFormat) : '' }}</span>
      </slot>
    </div>
    <div>
      <p style="color:white; margin:0px; height:27px;">t</p>
    </div>

    <!-- Card Rejected -->
    <div v-if='cardRejected' :key="cards.id" :endpoint='api.$.evedpaycard.cards'>
      <div v-if="cards.rejectReason">
        <ed-notice type='danger' icon='fas fa-exclamation-triangle'>
          {{ cards.rejectReason ? cards.rejectReason : '' }}
        </ed-notice>
      </div>
    </div>

    <div v-if="!cardRejected" :endpoint='api.$.evedpaycard.transactions'>
      <slot name='title' />

      <div v-if="cards" class="center" :key="cards.id">
        <h3><v-money>{{ cards.spent }}</v-money></h3>
        <h4>Balance: <v-money>{{ cards.balance }}</v-money></h4>
        <h4><router-link :to="{ path: '/evedpay/details', query: { ...$route.query, cardid: cardId } }"> CARD DETAILS</router-link></h4>
      </div>
      <div class='no-data' v-if='transactions && !transactions.length'>
        <div>
          <i class='icon fas fa-list'></i>
          <h3>No Transactions</h3>
          <p>Your transactions will appear here</p>
        </div>
      </div>
      <div v-for='(transaction, i) in transactions' @click="() => redirectTransaction(transaction)">
        <h6 v-if='showHeader(transaction, i)'>
          {{ headerText(transaction) }}
        </h6>
        <ed-notice class='transaction' :type='transactionStatusColor(transaction.status, transaction.reconcileStatus)'>
          <p class="amount"><v-money>{{ transaction.amount }}</v-money></p>
          <p class="merchant">{{ transaction.merchant }}</p>
          <div class="col-xs-9" style="padding:0" v-if="transaction.status === 'Pending'">
            <p class="date">{{ moment(transaction.date).format(dateFormat) }} | Pending</p>
          </div>
          <div class="col-xs-9" style="padding:0" v-else>
            <p class="date">{{ moment(transaction.date).format(dateFormat) }}</p>
          </div>
          <div class="col-xs-3 text-right" style="padding-right:2px;">
            <i v-if="transaction.receipt !== null" class="fas fa-camera" style="color: #4A90E2;"></i>
          </div>
        </ed-notice>
      </div>
    </div>
  </div>
</template>

<script>
import transactionDisplayMixin from './transactionDisplayMixin'

export default {
  alias: 'evedpaycardtransactions',
  mixins: [transactionDisplayMixin],
  data() {
    return {
      cardId: this.$route.query.cardid,
      cards: [],
      transactions: null,
      dateFormat: this.$user.sessionSettings.DateAndTimeFormats.MomentDateFormat
    }
  },
  created() {
    // Non-reactive token: invalidates in-flight loadCardData() responses that were
    // superseded by a newer call (same pattern as _modules/Programs/Card/index.vue).
    this.loadTokenCounter = 0
    this.loadCardData()
  },
  // The shell keeps this module alive (Layouts/shell/mobile.vue), so created() runs
  // once. Refetch on every re-entry: a different ?cardid needs different data, and
  // returning from a reconcile must pick up the moved row — this view has no
  // 'transaction-reconciled' listener, so a guarded refetch would show the stale
  // bucket. Same unconditional-refresh-on-activation pattern as
  // _modules/Programs/Card/index.vue.
  activated() {
    // The first activation fires together with created() on initial mount —
    // skip it to avoid a double fetch.
    if (!this._hasActivatedOnce) {
      this._hasActivatedOnce = true
      return
    }
    this.loadCardData()
  },
  computed: {
    moment() { return global.moment },
    card() {
      return this.cards && this.cards.id === this.$route.query.cardid ? this.cards : {}
    },
    cardRejected() {
      return this.card.rejectReason !== null
    }
  },
  methods: {
    loadCardData() {
      // Stale-response guard: with the unconditional activated() refetch, two calls
      // can be in flight at once (re-enter card B while card A still loads, or a
      // quick same-card bounce through a reconcile). Only the newest call may
      // assign — otherwise the older snapshot wins and renders the wrong card's
      // rows / the pre-reconcile buckets.
      const token = ++this.loadTokenCounter
      const CardId = this.$route.query.cardid
      this.cardId = CardId
      this.transactions = null
      this.cards = []
      this.api.forms.post(this.api.$.evedpaycard.transactions, {
        sort: 'TransactionGroup',
        dir: 'DESC',
        start: 0,
        limit: 1000,
        isLiveGrid: true,
        // Must be a JSON string: the legacy grid reads Request.Form["filter"] and deserializes it.
        // Passed as a raw object, qs.stringify expands it to filter[CardId]=...&filter[searchTerm]=,
        // so the server never sees a "filter" field and skips the CardId filter — returning every
        // card's transactions (the whole division for admins) and leaning entirely on the client filter.
        filter: JSON.stringify({
          CardId,
          searchTerm: ''
        })
      }).then(r => {
        if (token !== this.loadTokenCounter) {
          return
        }
        this.transactions = r.data.Data
          .filter(t => t.CardId === CardId)
          .map(transaction => ({
            merchant: transaction.Merchant,
            amount: transaction.Amount,
            date: transaction.TransactionDate,
            status: transaction.TransactionStatus,
            id: transaction.Id,
            cardNumber: transaction.CardNumber,
            reconcileStatus: transaction.ReconciliationStatus,
            holderId: transaction.HolderUserId,
            receipt: transaction.Receipt
          }))
        // Group rows by display header (Reconcile, Pending, Completed, Declined) so each header
        // renders exactly once — the server's TransactionGroup sort interleaves them. See
        // sortByDisplayGroup().
        this.sortByDisplayGroup()
      })
      this.api.forms.post(this.api.$.evedpaycard.cards, {
        sort: 'CardNumber',
        dir: 'DESC',
        start: 0,
        limit: 100,
        isLiveGrid: true,
        filter: {
          CardStatus: 'active',
          searchTerm: ''
        }
      }).then(r => {
        if (token !== this.loadTokenCounter) {
          return
        }
        const matchedCard = r.data.Data.find(card => card.Id === CardId)
        if (matchedCard) {
          this.cards = {
            name: matchedCard.Name || `EvedPay Card ...${matchedCard.CardNumber}`,
            holder: matchedCard.Holder,
            holderUserId: matchedCard.HolderUserID,
            division: matchedCard.Division,
            initialLoad: matchedCard.InitialLoad,
            spent: matchedCard.Spent,
            returned: 0,
            balance: matchedCard.Balance,
            id: matchedCard.Id,
            cardNumber: matchedCard.CardNumber,
            rejectReason: matchedCard.RejectionReason,
            createdDate: matchedCard.CreatedDate
          }
        }
      })
    }
  }
}

</script>

<style lang='scss' scoped>
@import "~@/assets/sass/_variables.scss";

.no-data {
  height: calc(100vh - 15em);
  width: 75%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  h3 {
    margin-top: 1.6em;
    margin-bottom: 0.2em;
    font-weight: 600;
  }
  h3, p, .icon {
    color: #4e2683;
  }
  p {
    line-height: 1.2em;
  }

  .icon {
    font-size: 5em;
    &:after {
      position: absolute;
      display: block;
      content: '';
      left: 50%;
      top: 50%;
      margin-top: -80px;
      margin-left: -80px;
      width: 160px;
      height: 160px;
      border-radius: 50%;
      background: rgba(78,38,131,0.15);
      z-index: -1;
    }
  }
}

.transaction {
  border-radius: 6px;
  border-top-width: 0px !important;
  border-right-width: 0px !important;
  border-bottom-width: 0px !important;
  box-shadow: 1px 3px 5px 0px rgba(0,0,0,0.15);
  >>> &.no-icon .icon-wrapper.no-icon {
    min-width: 0;
  }
}

.amount {
  font-weight: 600;
  float: right;
}
.merchant {
  font-weight: 600;
}
.date {
  font-size: 80%;
}

.center {
  display: flex;
  flex-direction: column;
  align-items: center; /* Horizontally center the content */
  justify-content: center; /* Vertically center the content */
}

.navless-layout-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.center-span {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/components/_modules/EvedPay/cardtransactions.vue